import { supabase, isSupabaseConfigured } from '../../../lib/supabase'

interface Stats {
  products: number
  blogs: number
  faqs: number
  testimonials: number
}

const quickLinks = [
  { path: '/admin/products',     label: 'Upravljaj proizvodima',    icon: 'ri-box-3-line',           color: 'bg-blue-50 text-blue-700 border-blue-200' },
  { path: '/admin/blog',         label: 'Upravljaj blogom',         icon: 'ri-article-line',          color: 'bg-purple-50 text-purple-700 border-purple-200' },
  { path: '/admin/homepage',     label: 'Uredi početnu stranicu',   icon: 'ri-home-4-line',           color: 'bg-green-50 text-green-700 border-green-200' },
  { path: '/admin/settings',     label: 'Tracking kodovi & SEO',    icon: 'ri-code-s-slash-line',     color: 'bg-amber-50 text-amber-700 border-amber-200' },
]

export default function AdminDashboard() {
  const [stats, setStats] = useState<Stats>({ products: 0, blogs: 0, faqs: 0, testimonials: 0 })

  useEffect(() => {
    if (!isSupabaseConfigured) {
      setStats({ products: 10, blogs: 6, faqs: 8, testimonials: 4 })
      return
    }
    Promise.all([
      supabase.from('cms_products').select('id', { count: 'exact', head: true }),
      supabase.from('cms_blog_posts').select('id', { count: 'exact', head: true }),
      supabase.from('cms_faqs').select('id', { count: 'exact', head: true }),
      supabase.from('cms_testimonials').select('id', { count: 'exact', head: true }),
    ]).then(([p, b, f, t]) => {
      setStats({
        products: p.count ?? 0,
        blogs: b.count ?? 0,
        faqs: f.count ?? 0,
        testimonials: t.count ?? 0,
      })
    })
  }, [])

  const statCards = [
    { label: 'Proizvoda',     value: stats.products,     icon: 'ri-box-3-line',           color: 'text-blue-600',   bg: 'bg-blue-50' },
    { label: 'Blog postova',  value: stats.blogs,        icon: 'ri-article-line',          color: 'text-purple-600', bg: 'bg-purple-50' },
    { label: 'FAQ pitanja',   value: stats.faqs,         icon: 'ri-question-line',         color: 'text-green-600',  bg: 'bg-green-50' },
    { label: 'Testimonijala', value: stats.testimonials, icon: 'ri-user-star-line',        color: 'text-amber-600',  bg: 'bg-amber-50' },
  ]

  return (
    <div className="space-y-6">
      {!isSupabaseConfigured && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex gap-3">
          <i className="ri-error-warning-line text-amber-600 text-xl mt-0.5 flex-shrink-0" />
          <div>
            <p className="font-semibold text-amber-800">Supabase nije podešen</p>
            <p className="text-amber-700 text-sm mt-0.5">
              Kreirajte <code className="bg-amber-100 px-1 rounded">.env</code> fajl na osnovu <code className="bg-amber-100 px-1 rounded">.env.example</code> i unesite Supabase URL i ključ.
              Zatim pokrenite SQL iz <code className="bg-amber-100 px-1 rounded">supabase/schema.sql</code> u vašem Supabase projektu.
            </p>
          </div>
        </div>
      )}

      {/* Stat cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map(s => (
          <div key={s.label} className="bg-white rounded-xl border border-gray-200 p-5">
            <div className={`w-10 h-10 ${s.bg} rounded-lg flex items-center justify-center mb-3`}>
              <i className={`${s.icon} ${s.color} text-lg`} />
            </div>
            <p className="text-2xl font-bold text-gray-900">{s.value}</p>
            <p className="text-sm text-gray-500 mt-0.5">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Quick actions */}
      <div>
        <h2 className="text-gray-900 font-semibold mb-3">Brze akcije</h2>
        <div className="grid sm:grid-cols-2 gap-3">
          {quickLinks.map(q => (
            <Link
              key={q.path}
              to={q.path}
              className={`flex items-center gap-3 p-4 rounded-xl border ${q.color} transition hover:shadow-sm`}
            >
              <i className={`${q.icon} text-xl`} />
              <span className="font-medium text-sm">{q.label}</span>
              <i className="ri-arrow-right-line ml-auto" />
            </Link>
          ))}
        </div>
      </div>

      {/* Info box */}
      <div className="bg-slate-900 rounded-xl p-6 text-white">
        <h3 className="font-semibold mb-2 flex items-center gap-2">
          <i className="ri-information-line text-blue-400" />
          Kako koristiti CMS
        </h3>
        <ul className="text-slate-300 text-sm space-y-1.5">
          <li>• <strong className="text-white">Proizvodi</strong> — dodajte, uredite ili obrišite proizvode na sajtu</li>
          <li>• <strong className="text-white">Blog</strong> — upravljajte blog postovima i kategorijama</li>
          <li>• <strong className="text-white">Početna</strong> — uredite sve sekcije landing stranice (hero, about, CTA...)</li>
          <li>• <strong className="text-white">FAQ</strong> — upravljajte čestim pitanjima</li>
          <li>• <strong className="text-white">Testimonijali</strong> — dodajte ili uredite utiske klijenata</li>
          <li>• <strong className="text-white">Podešavanja</strong> — SEO meta tagovi, Google Analytics, Facebook Pixel, GTM i drugi tracking kodovi</li>
        </ul>
      </div>
    </div>
  )
}
